import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useStaff } from "./StaffLayout";

function todayStr(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function ReportBox({ schedule, t, onSent }) {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(schedule.actual_start || "");
  const [end, setEnd] = useState(schedule.actual_end || "");
  const [note, setNote] = useState(schedule.report || "");
  const [done, setDone] = useState(Boolean(schedule.done));
  const [saving, setSaving] = useState(false);

  const send = async () => {
    setSaving(true);
    try {
      await api.put(`/staff/schedules/${schedule.id}/report`, { actual_start: start, actual_end: end, done, note });
      onSent();
    } catch {} finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none focus:border-accent";
  return (
    <div className="mt-3 border-t border-line pt-3">
      <button data-testid={`staff-report-toggle-${schedule.id}`} onClick={() => setOpen((v) => !v)} className="text-sm font-bold text-accent">
        {t("report_title")} {open ? "▲" : "▼"}
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-wider text-gray-500">{t("actual_start")}</label>
              <input data-testid={`staff-report-start-${schedule.id}`} type="time" value={start} onChange={(e) => setStart(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-wider text-gray-500">{t("actual_end")}</label>
              <input data-testid={`staff-report-end-${schedule.id}`} type="time" value={end} onChange={(e) => setEnd(e.target.value)} className={inputCls} />
            </div>
          </div>
          <textarea data-testid={`staff-report-note-${schedule.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("report_note")} rows={2} className={inputCls} />
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input data-testid={`staff-report-done-${schedule.id}`} type="checkbox" checked={done} onChange={(e) => setDone(e.target.checked)} className="h-4 w-4 accent-[#ff8c1a]" />
            {t("done_label")}
          </label>
          <button data-testid={`staff-report-send-${schedule.id}`} onClick={send} disabled={saving} className="w-full bg-accent py-3 text-sm font-bold text-ink transition-colors hover:bg-accent-hover disabled:opacity-40">
            {saving ? "..." : t("send_report")}
          </button>
        </div>
      )}
    </div>
  );
}

export function ScheduleCard({ s, t, showReport = true, onReported }) {
  const typeLabel = s.type === "off" ? t("type_off") : s.type === "leave" ? t("type_leave") : t("type_work");
  const typeColor = s.type === "work" ? "text-accent" : "text-gray-400";
  return (
    <div data-testid={`staff-schedule-card-${s.id}`} className="border border-line bg-coal p-4">
      <div className="flex items-center justify-between">
        <span className="font-mono text-sm text-white">
          {s.date} {s.all_day ? t("all_day") : `${s.start_time || ""}${s.end_time ? " - " + s.end_time : ""}`}
        </span>
        <span className={`text-xs font-bold ${typeColor}`}>{typeLabel}</span>
      </div>
      <p className="mt-1.5 text-base font-bold text-white">{s.title}</p>
      {s.project && <p className="mt-0.5 text-xs text-gray-400">[{s.project.code || "-"}] {s.project.name}</p>}
      {s.site && (
        <div className="mt-2 space-y-1 text-xs text-gray-400">
          {s.site.name && <p data-testid={`staff-card-site-${s.id}`}>■ {s.site.name}</p>}
          {s.site.meeting_time && <p>{t("meeting")}: {s.site.meeting_time}{s.site.meeting_point ? ` / ${s.site.meeting_point}` : ""}</p>}
          {s.site.address && <p>{t("site_address")}: {s.site.address}</p>}
          {s.site.safety_notes && <p className="text-amber-400/90">⚠ {s.site.safety_notes}</p>}
        </div>
      )}
      {s.description && <p className="mt-2 whitespace-pre-wrap text-sm text-gray-300">{s.description}</p>}
      {s.instructions && s.instructions.length > 0 && (
        <div className="mt-2 border-l-2 border-accent/50 pl-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-accent">{t("instructions")}</p>
          {s.instructions.map((ins) => (
            <div key={ins.id} className="mt-1 text-xs text-gray-300">
              <p className="font-bold text-white">{ins.title}</p>
              {ins.description && <p className="whitespace-pre-wrap">{ins.description}</p>}
              {ins.tools && <p className="text-gray-400">{t("tools_to_bring")}: {ins.tools}</p>}
              {ins.cautions && <p className="text-amber-400/90">⚠ {ins.cautions}</p>}
            </div>
          ))}
        </div>
      )}
      {s.done && <p className="mt-2 text-xs font-bold text-emerald-400">✓ {t("done_label")}</p>}
      {showReport && s.type === "work" && onReported && <ReportBox schedule={s} t={t} onSent={onReported} />}
    </div>
  );
}

export default function StaffHome() {
  const { t, unread, setUnread } = useStaff();
  const [schedules, setSchedules] = useState([]);
  const [notifications, setNotifications] = useState([]);

  const load = () => {
    api.get("/staff/schedules", { params: { date_from: todayStr(), date_to: todayStr(1) } }).then((r) => setSchedules(r.data)).catch(() => {});
    api.get("/staff/notifications").then((r) => setNotifications(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const today = useMemo(() => schedules.filter((s) => s.date === todayStr()), [schedules]);
  const tomorrow = useMemo(() => schedules.filter((s) => s.date === todayStr(1)), [schedules]);

  const markRead = async (id) => {
    await api.post(`/staff/notifications/${id}/read`).catch(() => {});
    setNotifications((list) => list.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread(Math.max(0, unread - 1));
  };

  return (
    <div data-testid="staff-home" className="mx-auto max-w-lg space-y-6 px-4 py-6">
      <section>
        <h2 data-testid="staff-today-heading" className="font-mincho text-lg font-bold text-white">{t("today")}</h2>
        <div className="mt-3 space-y-3">
          {today.length === 0 && <p data-testid="staff-today-empty" className="text-sm text-gray-500">{t("no_plans")}</p>}
          {today.map((s) => <ScheduleCard key={s.id} s={s} t={t} onReported={load} />)}
        </div>
      </section>

      <section>
        <h2 data-testid="staff-tomorrow-heading" className="font-mincho text-lg font-bold text-white">{t("tomorrow")}</h2>
        <div className="mt-3 space-y-3">
          {tomorrow.length === 0 && <p data-testid="staff-tomorrow-empty" className="text-sm text-gray-500">{t("no_plans")}</p>}
          {tomorrow.map((s) => <ScheduleCard key={s.id} s={s} t={t} showReport={false} />)}
        </div>
      </section>

      <section id="notifications">
        <h2 data-testid="staff-notifications-heading" className="font-mincho text-lg font-bold text-white">{t("notifications")}</h2>
        <div className="mt-3 space-y-2">
          {notifications.length === 0 && <p data-testid="staff-notifications-empty" className="text-sm text-gray-500">{t("no_notifications")}</p>}
          {notifications.map((n) => (
            <button
              key={n.id}
              data-testid={`staff-notification-${n.id}`}
              onClick={() => markRead(n.id)}
              className={`w-full border p-3 text-left transition-colors ${n.read ? "border-line bg-coal/50 text-gray-500" : "border-accent/40 bg-coal text-white"}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold">{n.title}</span>
                {!n.read && <span className="h-2 w-2 rounded-full bg-accent" />}
              </div>
              <p className="mt-0.5 text-xs opacity-80">{n.body}</p>
              <p className="mt-1 font-mono text-[10px] text-gray-600">{(n.created_at || "").replace("T", " ").slice(0, 16)}</p>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
